import { useState, useEffect, useCallback } from "react";
import {
  fetchMFAPreference,
  updateMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
} from "aws-amplify/auth";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../shared/button";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { Spinner } from "../shared/spinner";
import "./mfa-section.css";

type MfaState = "loading" | "disabled" | "setup" | "enabled";

export function MfaSection() {
  const [state, setState] = useState<MfaState>("loading");
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupUri, setSetupUri] = useState<string>("");
  const [sharedSecret, setSharedSecret] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [verifying, setVerifying] = useState(false);

  const loadPreference = useCallback(async () => {
    try {
      const pref = await fetchMFAPreference();
      if (pref.preferred === "TOTP" || pref.enabled?.includes("TOTP")) {
        setState("enabled");
      } else {
        setState("disabled");
      }
    } catch {
      // Treat any error as "MFA not configured" — show enable button.
      setState("disabled");
    }
  }, []);

  useEffect(() => {
    loadPreference();
  }, [loadPreference]);

  async function beginSetup() {
    setError(null);
    try {
      const result = await setUpTOTP();
      setSharedSecret(result.sharedSecret);
      const uri = result.getSetupUri("Simple Cash Discount", undefined);
      setSetupUri(uri.toString());
      setState("setup");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start MFA setup");
    }
  }

  async function verifyAndEnable() {
    setVerifying(true);
    setError(null);
    try {
      await verifyTOTPSetup({ code: verifyCode });
      await updateMFAPreference({ totp: "PREFERRED" });
      setVerifyCode("");
      setSharedSecret("");
      setSetupUri("");
      setState("enabled");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setVerifying(false);
    }
  }

  function cancelSetup() {
    setError(null);
    setVerifyCode("");
    setSharedSecret("");
    setSetupUri("");
    setState("disabled");
  }

  async function disableMfa() {
    setError(null);
    try {
      await updateMFAPreference({ totp: "DISABLED" });
      setState("disabled");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable MFA");
    }
  }

  return (
    <div className="card">
      <div className="clabel">Authenticator App</div>
      {error && <div className="profile-error">{error}</div>}

      {state === "loading" && (
        <div style={{ padding: "16px 0", textAlign: "center" }}>
          <Spinner />
        </div>
      )}

      {state === "disabled" && (
        <>
          <div className="mfa-helper">
            Add an extra layer of security by requiring a 6-digit code from an
            authenticator app (Google Authenticator, 1Password, Authy) at sign-in.
          </div>
          <Button variant="gradient" onClick={beginSetup}>
            Enable Authenticator App
          </Button>
        </>
      )}

      {state === "enabled" && (
        <>
          <div className="mfa-enabled-row">
            <span className="mfa-enabled-check">&#x2705;</span>
            <span className="mfa-enabled-text">Authenticator app enabled</span>
          </div>
          <Button
            variant="red"
            onClick={() => setShowDisableConfirm(true)}
          >
            Disable
          </Button>
        </>
      )}

      {state === "setup" && (
        <>
          <div className="mfa-setup-step-label">Step 1 — Scan in your app</div>
          <div className="mfa-qr">
            <QRCodeSVG value={setupUri} size={180} />
          </div>
          <div className="mfa-setup-step-label">Or enter this secret manually</div>
          <div className="mfa-secret-code">{sharedSecret}</div>

          <div className="mfa-setup-step-label">Step 2 — Enter the 6-digit code</div>
          <div className="profile-field">
            <label className="profile-label" htmlFor="mfa-verify">
              6-digit code
            </label>
            <input
              id="mfa-verify"
              className="profile-input"
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="123456"
            />
          </div>

          <div className="mfa-setup-actions">
            <Button variant="outline" onClick={cancelSetup}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              onClick={verifyAndEnable}
              disabled={verifying || verifyCode.length !== 6}
            >
              {verifying ? "Verifying…" : "Verify and enable"}
            </Button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={showDisableConfirm}
        title="Disable authenticator app?"
        message="You'll no longer be required to enter a 6-digit code when signing in. You can re-enable it at any time."
        confirmLabel="Disable"
        variant="destructive"
        onConfirm={() => {
          setShowDisableConfirm(false);
          disableMfa();
        }}
        onCancel={() => setShowDisableConfirm(false)}
      />
    </div>
  );
}
